// Plan page copy. Purpose builds the plan from fixed templates picked by the answers, not
// by AI (four walk-throughs on 31 Oct 2026, stepcase/timeblock#1273): Q1 picks the plan,
// Q5 picks the opening "noticed" sentence and the first explaining screen. Q7 and Q9 pick
// the second and third "built from your answers" tags.

import {
  GOAL_VALUES,
  OBSTACLE_VALUES,
  type Answers,
  type GoalValue,
  type ObstacleValue,
} from './questions';

export const COACH_NAME = 'LifeHack AI Coach';
export const FRAMEWORK_NAME = 'Full Life Framework';

interface PlanTemplate {
  planName: string;
  startingPoint: { name: string; line: string };
  /** Follows the Q5 opener in "Here's what we noticed", paragraph 1. */
  noticedFollowUp: string;
  /** "Here's what we noticed", paragraph 2. */
  noticedDetail: string;
  goalTag: string;
  includes: string[];
  includesFocusTag: string;
  path: { label: string; text: string }[];
  firstWeek: { day: number; text: string }[];
  /** The goal the app account starts with, from this plan. */
  appGoalTitle: string;
}

export const PLAN_TEMPLATES: Record<GoalValue, PlanTemplate> = {
  habits: {
    planName: 'Follow-Through Plan',
    startingPoint: {
      name: 'The Momentum Builder',
      line: 'You know what to do, you need the system to make it stick',
    },
    noticedFollowUp:
      'Your plan begins by turning change into a repeatable rhythm instead of another all-or-nothing push.',
    noticedDetail: `You've got ambitions that outpace your systems. You start strong but life's demands pull you off course before habits can take root. ${COACH_NAME} builds your habit stack around your real life, not an idealized version of it.`,
    goalTag: 'Habit-building',
    includes: [
      'An AI coach that holds you accountable without guilt or judgment',
      'Habit stacking designed around your actual schedule, not an ideal one',
      'Daily conversations that reinforce your momentum and celebrate wins',
      "Built on Leon Ho's philosophy: focus on what matters, drop the rest",
    ],
    includesFocusTag: 'habit systems & accountability',
    path: [
      { label: 'Week 1', text: 'Lock in your anchor habit with daily accountability' },
      { label: 'Week 2', text: 'Build your first streak and stack habit #2' },
      { label: 'Month 1', text: 'A system that runs on autopilot, habits that stick' },
    ],
    firstWeek: [
      { day: 1, text: 'Choose your anchor habit (the one that changes everything)' },
      { day: 3, text: 'Stack it into your existing routine' },
      { day: 5, text: 'Track your first 3-day streak' },
      { day: 7, text: 'Celebrate + expand to habit #2' },
    ],
    appGoalTitle: 'Build one anchor habit that sticks',
  },
  stress: {
    planName: 'Calm Reset Plan',
    startingPoint: {
      name: 'The Calm Seeker',
      line: "You're seeking peace in a world that won't slow down",
    },
    noticedFollowUp:
      'Your plan begins by helping you notice the pressure pattern earlier, before it turns into a full stress spiral.',
    noticedDetail: `You're carrying the weight of too many responsibilities, and your mind rarely gets a break. You know you need to slow down, but everything feels urgent. ${COACH_NAME} will help you identify what actually deserves your energy, and build a daily practice that brings you back to center.`,
    goalTag: 'Stress relief',
    includes: [
      'Daily check-ins that help you spot stress triggers before they spiral',
      "Guided exercises based on LifeHack's values-first framework",
      'A coach that actually remembers your patterns and adapts to you',
      'Not generic meditation, real conversations about your real life',
    ],
    includesFocusTag: 'emotional regulation & calm',
    path: [
      { label: 'Week 1', text: 'Identify your stress patterns and begin daily check-ins' },
      { label: 'Week 2', text: 'Notice shifts in how you respond to pressure' },
      { label: 'Month 1', text: 'A calmer, more controlled relationship with stress' },
    ],
    firstWeek: [
      { day: 1, text: 'Identify your top 3 stress triggers' },
      { day: 3, text: 'Learn your personal calm-down sequence' },
      { day: 5, text: 'Build a 5-minute wind-down ritual' },
      { day: 7, text: "First stress audit, see what's shifting" },
    ],
    appGoalTitle: 'Catch stress earlier and build a daily calm practice',
  },
  direction: {
    planName: 'Clarity Plan',
    startingPoint: {
      name: 'The Direction Finder',
      line: "You're searching for the path that's uniquely yours",
    },
    noticedFollowUp:
      'Your plan begins by turning uncertainty into a small next experiment instead of asking you to solve your whole life at once.',
    noticedDetail: `You're busy doing things but unsure if any of it leads somewhere meaningful. The noise of daily life drowns out the signal. ${COACH_NAME} helps you cut through the clutter and connect with what actually matters to you, not what you think should matter.`,
    goalTag: 'Direction and purpose',
    includes: [
      "A structured values exploration based on LifeHack's proven framework",
      "Daily prompts that surface clarity you already have but can't access",
      'A coach that tracks your patterns and reflects insights back to you',
      'Not more information, a genuine thinking partner for your life',
    ],
    includesFocusTag: 'purpose discovery & goal-setting',
    path: [
      { label: 'Week 1', text: 'Map your core values and what actually matters' },
      { label: 'Week 2', text: 'Start noticing patterns in what energizes you' },
      { label: 'Month 1', text: 'Real clarity on your direction, not just ideas, conviction' },
    ],
    firstWeek: [
      { day: 1, text: 'Map your values, what actually matters to you' },
      { day: 3, text: "Identify what you're tolerating that doesn't align" },
      { day: 5, text: "Design your first 'purpose experiment'" },
      { day: 7, text: 'Clarity check-in, patterns are emerging' },
    ],
    appGoalTitle: 'Get clear on what matters and run my first purpose experiment',
  },
  transition: {
    planName: 'Life Transition Plan',
    // Purpose reuses the direction plan's name for transitions.
    startingPoint: {
      name: 'The Direction Finder',
      line: "You're navigating change and need a grounded next step",
    },
    noticedFollowUp:
      'Your plan begins by making the transition concrete enough to act on, without asking you to solve the whole next chapter today.',
    noticedDetail: `You are in the middle of change, and every decision feels like it carries extra weight. ${COACH_NAME} helps you separate what actually needs attention from the noise around it, then turn the transition into one concrete next step.`,
    goalTag: 'Life transition support',
    includes: [
      'A structured way to sort what is changing from what still matters',
      'Daily prompts that turn big uncertainty into a concrete next step',
      'A coach that remembers your transition and tracks what you are learning',
      'Not generic advice, a thinking partner for the decision in front of you',
    ],
    includesFocusTag: 'life transition clarity & next-step planning',
    path: [
      { label: 'Week 1', text: 'Map the transition and choose one grounded next step' },
      { label: 'Week 2', text: 'Start testing small decisions instead of waiting for certainty' },
      { label: 'Month 1', text: 'A clearer way through the change, with less second-guessing' },
    ],
    firstWeek: [
      { day: 1, text: 'Map what is changing and what still matters' },
      { day: 3, text: 'Name the decision you are avoiding' },
      { day: 5, text: 'Try one small transition experiment' },
      { day: 7, text: 'Review what felt clearer, lighter, or still unresolved' },
    ],
    appGoalTitle: 'Turn my life transition into one grounded next step',
  },
};

/** "Here's what we noticed", paragraph 1, sentence 1, by the Q5 answer. */
export const OBSTACLE_OPENERS: Record<ObstacleValue, string> = {
  motivation:
    'You are not short on effort. You tend to start with intensity, then lose rhythm, so the plan starts by making progress easier to repeat.',
  'no-time':
    "You are not short on intent. Your days are already full, so the plan starts with steps small enough to fit into the life you have, not the one you're waiting for.",
  'one-size':
    "You've tried the generic advice, and it never fit your life. So the plan starts from what you told us, not from a template written for someone else.",
  accountability:
    "You can start on your own, but it's hard to keep going alone. So the plan starts with a daily check-in, so someone notices when you slip and helps you restart.",
};

export interface InsightScreen {
  title: string;
  body: string;
  points?: string[];
  footnote?: string;
}

/** Explaining screen 1 follows the Q5 answer; Purpose's wording is the motivation one. */
export const FIRST_INSIGHT_BY_OBSTACLE: Record<ObstacleValue, InsightScreen> = {
  motivation: {
    title: 'That tells us your plan should start with rhythm, not intensity',
    body: "If consistency is what breaks, your first step needs to be small enough to repeat. We'll use that to shape your plan.",
  },
  'no-time': {
    title: 'That tells us your plan should fit the life you already have',
    body: "If time is what runs out, your first step needs to fit inside a busy day. We'll use that to shape your plan.",
  },
  'one-size': {
    title: 'That tells us your plan should start from you, not a template',
    body: "If generic advice never fit, your first step needs to come from what you told us. We'll use that to shape your plan.",
  },
  accountability: {
    title: 'That tells us your plan should start with someone in your corner',
    body: "If it's hard to keep going alone, your first step needs a daily check-in that notices when you slip. We'll use that to shape your plan.",
  },
};

export const SECOND_INSIGHT: InsightScreen = {
  title: 'How your answers become momentum',
  body: '',
  points: [
    'Your answers shape the first plan you see after the quiz.',
    'Your coach uses that context to guide conversations, reflections and next steps.',
    'Small daily check-ins help the plan compound across days.',
  ],
  footnote: `Built from LifeHack's ${FRAMEWORK_NAME}.`,
};

export const THIRD_INSIGHT: InsightScreen = {
  title: 'Your plan continues after checkout',
  body: 'LifeHack is available on iOS and the web app, so your first week can move from quiz insight to daily action without starting over. The trial unlocks the full coach experience.',
};

const PROCESSING_TAGS: Record<string, string> = {
  talking: 'Talk-it-through coaching',
  writing: 'Journaling-based coaching',
  quiet: 'Quiet reflection prompts',
  action: 'Learn-by-doing coaching',
};

const DAILY_TIME_TAGS: Record<string, string> = {
  '5': '5-minute power sessions',
  '10': '10-minute daily sessions',
  '15-20': '15-20 minute deep sessions',
  varies: 'Flexible daily sessions',
};

export interface FollowThroughPlan {
  goal: GoalValue;
  obstacle: ObstacleValue;
  planName: string;
  startingPoint: { name: string; line: string };
  noticed: [string, string];
  builtFromTags: [string, string, string];
  includes: string[];
  includesTags: [string, string, string];
  path: { label: string; text: string }[];
  firstWeek: { day: number; text: string }[];
  appGoalTitle: string;
}

function single(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function isGoal(value: string | undefined): value is GoalValue {
  return (GOAL_VALUES as readonly string[]).includes(value ?? '');
}

function isObstacle(value: string | undefined): value is ObstacleValue {
  return (OBSTACLE_VALUES as readonly string[]).includes(value ?? '');
}

export function goalFromAnswers(answers: Answers): GoalValue {
  const goal = single(answers.goal);
  if (!isGoal(goal)) throw new Error(`Follow-Through Plan answers have no known goal: ${goal ?? 'none'}`);
  return goal;
}

export function obstacleFromAnswers(answers: Answers): ObstacleValue {
  const obstacle = single(answers.obstacle);
  if (!isObstacle(obstacle)) {
    throw new Error(`Follow-Through Plan answers have no known obstacle: ${obstacle ?? 'none'}`);
  }
  return obstacle;
}

function tagFor(tags: Record<string, string>, questionId: 'processing' | 'dailyTime', answers: Answers): string {
  const value = single(answers[questionId]);
  const tag = value ? tags[value] : undefined;
  if (!tag) throw new Error(`Follow-Through Plan answers have no known ${questionId}: ${value ?? 'none'}`);
  return tag;
}

export function buildFollowThroughPlan(answers: Answers): FollowThroughPlan {
  const goal = goalFromAnswers(answers);
  const obstacle = obstacleFromAnswers(answers);
  const template = PLAN_TEMPLATES[goal];
  const processingTag = tagFor(PROCESSING_TAGS, 'processing', answers);
  const timeTag = tagFor(DAILY_TIME_TAGS, 'dailyTime', answers);

  return {
    goal,
    obstacle,
    planName: template.planName,
    startingPoint: template.startingPoint,
    noticed: [`${OBSTACLE_OPENERS[obstacle]} ${template.noticedFollowUp}`, template.noticedDetail],
    builtFromTags: [template.goalTag, processingTag, timeTag],
    includes: template.includes,
    includesTags: ['conversational check-ins', template.includesFocusTag, timeTag.toLowerCase()],
    path: template.path,
    firstWeek: template.firstWeek,
    appGoalTitle: template.appGoalTitle,
  };
}
